import { useCallback, useEffect, useRef } from 'react';
import { Flex, List, RemoteImage, Typography, type ListRef } from '@banhala/ui-kit';
import type { AssembleRadioFilterPossibleItem } from '../types';
import { bridge, isNotEmpty } from '../../../../../utils';
import type { AnyObject } from '../../../../../types';

interface RadioFilterChipsProps {
  items: AssembleRadioFilterPossibleItem[];
  selectedQueryValue: string;
  onSelect: (queryValue: string) => void;
  logging: AnyObject;
  logDisabled?: boolean;
}

// iOS RadioChipFilterCell 색상 (isAssembleFilter = true)
const CHIP_SIZE = 36;
const IMAGE_SIZE = 28;
const BORDER_RADIUS = CHIP_SIZE / 2;

/** 라디오 필터 칩 — 리뷰어 탭 (iOS RadioChipFilterCell 대응) */
export const RadioFilterChips = ({
  items,
  selectedQueryValue,
  onSelect,
  logging,
  logDisabled,
}: RadioFilterChipsProps) => {
  const listRef = useRef<ListRef<AssembleRadioFilterPossibleItem>>(null);

  const handlePress = useCallback(
    (item: AssembleRadioFilterPossibleItem) => {
      if (!logDisabled) {
        bridge.logAnalyticsEvent({
          name: 'CLICK_CAROUSEL_FILTER',
          analytics: {
            ...logging.analytics,
            ...item.logging?.analytics,
            SELECTED_ITEM: item.text,
          },
          ably_only_log: {
            ...logging.ably_only_log,
            ...item.logging?.ably_only_log,
          },
        });
      }

      onSelect(item.query_value);
      const index = items.indexOf(item);
      if (index >= 0) {
        listRef.current?.scrollToIndex({
          index,
          animated: true,
          viewOffset: 150,
        });
      }
    },
    [onSelect, items, logging, logDisabled],
  );

  const renderItem = useCallback(
    ({ item }: { item: AssembleRadioFilterPossibleItem }) => {
      const isSelected = item.query_value === selectedQueryValue;
      const hasImage = isNotEmpty(item.image);

      return (
        <Flex
          height={CHIP_SIZE}
          borderRadius={BORDER_RADIUS}
          borderWidth={1}
          borderColor={isSelected ? 'border_slate_primary' : 'border_tertiary'}
          backgroundColor={isSelected ? 'bg_slate_primary' : 'bg_primary'}
          onPress={() => handlePress(item)}
        >
          <Flex.Row alignItems="center" height={CHIP_SIZE} padding={{ left: hasImage ? 4 : 12, right: 12 }}>
            {hasImage && (
              <Flex
                width={IMAGE_SIZE}
                height={IMAGE_SIZE}
                borderRadius={IMAGE_SIZE / 2}
                overflow="hidden"
                margin={{ right: 4 }}
              >
                <RemoteImage uri={item.image!} width={IMAGE_SIZE} height={IMAGE_SIZE} />
              </Flex>
            )}
            <Typography
              type="subtitle3"
              color={isSelected ? 'content_always_white' : 'content_secondary'}
            >
              {item.text}
            </Typography>
          </Flex.Row>
        </Flex>
      );
    },
    [selectedQueryValue, handlePress],
  );

  // 리뷰카드 스와이프로 selectedQueryValue가 변경되면 해당 칩으로 자동 스크롤
  const prevIndexRef = useRef(0);
  useEffect(() => {
    const index = items.findIndex(i => i.query_value === selectedQueryValue);
    if (index >= 0) {
      const isAdjacent = Math.abs(index - prevIndexRef.current) <= 1;
      listRef.current?.scrollToIndex({
        index,
        animated: isAdjacent,
        viewOffset: 150,
      });
      prevIndexRef.current = index;
    }
  }, [selectedQueryValue, items]);

  if (items.length <= 1) return null;

  return (
    <Flex padding={{ vertical: 12 }}>
      <List<AssembleRadioFilterPossibleItem>
        ref={listRef}
        horizontal
        data={items}
        renderItem={renderItem}
        keyExtractor={(item, index) => `radio-chip-${index}-${item.query_value}`}
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={contentContainerStyle}
      />
    </Flex>
  );
};

const contentContainerStyle = {
  paddingHorizontal: 16,
  gap: 8,
};
